/**
 * Data layer for `platform-fee-monthly-manual-bank-transfer`. Thin typed fetch wrappers over /api/v1/platform-fee-monthly-manual-bank-transfer.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PlatformFeeMonthlyManualBankTransferRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPlatformFeeMonthlyManualBankTransferResponse {
	count: number;
	items: PlatformFeeMonthlyManualBankTransferRow[];
}

export interface CreatePlatformFeeMonthlyManualBankTransferBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/platform-fee-monthly-manual-bank-transfer";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPlatformFeeMonthlyManualBankTransferResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPlatformFeeMonthlyManualBankTransferResponse>(res);
}

export async function create(body: CreatePlatformFeeMonthlyManualBankTransferBody): Promise<PlatformFeeMonthlyManualBankTransferRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PlatformFeeMonthlyManualBankTransferRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const platformFeeMonthlyManualBankTransferQueryKeys = {
	all: ["platform-fee-monthly-manual-bank-transfer"] as const,
	list: () => [...platformFeeMonthlyManualBankTransferQueryKeys.all] as const,
	detail: (id: string) => [...platformFeeMonthlyManualBankTransferQueryKeys.all, id] as const,
};
